import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Grid,
  Divider,
  Skeleton,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ErrorOutline } from "@mui/icons-material";
import { useOrderHistoryDetails } from "../hooks/useOrderHistory";
import { elevation } from "../styles/shadows";
import ModalOrderDetail from "../components/modals/ModalOrderDetail";
import ModalActiveESim from "../components/modals/ModalActiveESim";
import { OrderHistoryDetaiModel } from "../repositories/history.repository";
import BackButton from "../components/common/BackButton";
const formatAmount = (amount: string | number, currency: string = "LAK") =>
  new Intl.NumberFormat("lo-LA", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const OrderDetailPage = () => {
  const { id, channelid } = useParams<{ id: string; channelid: string }>();
  const {
    data: order,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useOrderHistoryDetails(id, channelid || "");
  const [selected, setSelected] = React.useState<any | null>(null);
  const [selectedActive, setSelectedActive] = React.useState<any | null>(null);

  if (!id) {
    return <Navigate to="/purchase" replace />;
  }

  if (isLoading || isFetching) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton
          variant="rectangular"
          height={160}
          sx={{ mt: 2, borderRadius: 3 }}
        />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Card
          sx={{
            borderRadius: 3,
            p: 3,
            border: `1px solid ${alpha("#d32f2f", 0.3)}`,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <ErrorOutline color="error" />
            <Stack>
              <Typography
                variant="subtitle1"
                fontWeight={900}
                color="error.main"
              >
                Failed to load order
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error.message}
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (!order) return null;

  // Normalize failure-like statuses
  const failedStatuses = [
    "pending",
    "failed",
    "canceled",
    "cancelled",
    "void",
    "timeout",
    "expired",
    "declined",
    "rejected",
    "error",
  ];
  const isFailed = failedStatuses.includes(
    (order.paymentStatus || "").toLowerCase(),
  );

  const handleSelectSim = (
    event: React.MouseEvent<HTMLButtonElement>,
    pkg: OrderHistoryDetaiModel,
  ) => {
    event.preventDefault();
    const eSimPlan = {
      planName: pkg.packageName,
      msisdn: pkg.msisdn,
      qrData: pkg.esimQR,
    };

    setSelected(eSimPlan);
  };
  const handleClose = () => setSelected(null);

  const handleActiveESim = (
    event: React.MouseEvent<HTMLButtonElement>,
    pkg: OrderHistoryDetaiModel,
  ) => {
    event.preventDefault();
    const eSimPlan = {
      msisdn: pkg.msisdn,
    };

    setSelectedActive(eSimPlan);
  };
  const handleActiveClose = () => setSelectedActive(null);

  return (
    <Box sx={{ minHeight: "100vh", py: 4 }}>
      <Container maxWidth="md">
        {/* Order Header */}
        <Stack spacing={0.5} mb={4}>
          <BackButton path="/purchase" />

          <Typography variant="h4" fontWeight={900}>
            Order #{order.orderId}
          </Typography>
          <Typography color="text.secondary">
            Ref: {order.paymentRef} ·{" "}
            {new Date(order.orderDate).toLocaleString()}
          </Typography>
          <Typography color="text.secondary">
            Customer: {order.customerName}
          </Typography>
        </Stack>

        {/* Order Summary */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          }}
        >
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Payment Method
                </Typography>
                <Typography fontWeight={700}>{order.paymentChannel}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={700}>
                    {order.paymentStatus}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography fontWeight={900} color="primary">
                  {formatAmount(order.amount)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Details */}
        {isFailed ? (
          // FAILED: show list summary without MSISDN and without eSIM QR
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                Order Items
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                {order.orderDetails.map((pkg: any, idx: number) => (
                  <ListItem
                    key={idx}
                    disableGutters
                    sx={{
                      px: 0,
                      py: 1.25,
                      "&:not(:last-of-type)": {
                        borderBottom: (t) => `1px dashed ${t.palette.divider}`,
                      },
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        variant: "subtitle1",
                        fontWeight: 700,
                      }}
                      primary={pkg.packageName}
                      secondaryTypographyProps={{ component: "div" }}
                      secondary={
                        <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
                          <Chip
                            size="small"
                            label={`Price: ${formatAmount(pkg.price)}`}
                          />
                          {pkg.dataQuota && (
                            <Chip
                              size="small"
                              label={`Data: ${pkg.dataQuota}`}
                            />
                          )}
                          {pkg.voiceQuota && (
                            <Chip
                              size="small"
                              label={`Voice: ${pkg.voiceQuota}`}
                            />
                          )}
                          {pkg.dayQuota && (
                            <Chip
                              size="small"
                              label={`Validity: ${pkg.dayQuota} days`}
                            />
                          )}
                          {pkg.areaService && (
                            <Chip
                              size="small"
                              label={`Area: ${pkg.areaService}`}
                            />
                          )}
                          {/* intentionally NO MSISDN */}
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        ) : (
          // SUCCESS/OTHER: render full package cards (including MSISDN & QR)
          <Stack spacing={3}>
            {order.orderDetails.map((pkg: any, idx: number) => (
              <Card
                key={idx}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{ height: 3, bgcolor: "primary.main", opacity: 0.8 }}
                />
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="h6" fontWeight={800}>
                      {pkg.packageName}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          MSISDN / IMSI
                        </Typography>
                        <Typography fontWeight={700}>{pkg.msisdn}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Price
                        </Typography>
                        <Typography fontWeight={700}>
                          {formatAmount(pkg.price)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Data Quota
                        </Typography>
                        <Typography fontWeight={700}>
                          {pkg.dataQuota}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Voice Quota
                        </Typography>
                        <Typography fontWeight={700}>
                          {pkg.voiceQuota}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Validity
                        </Typography>
                        <Typography fontWeight={700}>
                          {pkg.dayQuota} days
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Area
                        </Typography>
                        <Typography fontWeight={700}>
                          {pkg.areaService}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        sx={{ width: "100%", mt: 2, boxShadow: elevation[2] }}
                        onClick={(event) => handleSelectSim(event, pkg)}
                      >
                        Install eSim
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        sx={{ width: "100%", mt: 2, boxShadow: elevation[2] }}
                        onClick={(event) => handleActiveESim(event, pkg)}
                      >
                        Active eSim
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Container>
      <ModalOrderDetail
        open={Boolean(selected)}
        plan={selected}
        onClose={handleClose}
      />
      <ModalActiveESim
        open={Boolean(selectedActive)}
        plan={selectedActive}
        onClose={handleActiveClose}
        onActive={() => {}}
      />
    </Box>
  );
};

export default OrderDetailPage;
